import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Card, CardContent, Typography, Box, Button, Chip, Stack,
  IconButton, LinearProgress, Menu, MenuItem, Checkbox, Tooltip,
  Grid, Badge, TextField, InputAdornment, CircularProgress,
} from '@mui/material';
import {
  Campaign as CampaignIcon, Group as GroupIcon, Biotech as LabIcon, Person as PersonIcon, TrendingUp as TrendingUpIcon,
  Edit as EditIcon,
  Comment as CommentIcon,
  Send as SendIcon, GroupAdd as GroupAddIcon,
  Groups,
  Close as CloseIcon,
  VisibilityOff as VisibilityOffIcon,
  Inventory2Outlined as ArchiveIcon,
  TravelExplore as DiscoverIcon,
  HelpOutline as HelpOutlineIcon,
  Search as SearchIcon,
  ExpandMore as ExpandMoreIcon,
} from '@mui/icons-material';
import { styled, useTheme, alpha } from '@mui/material/styles';
import { useParams, useNavigate } from 'react-router-dom';
import { commonStyles } from 'shared/styles/enhancedTheme';
import PageContainer from 'components/common/layout/PageContainer';
import PageHeader from 'components/common/layout/PageHeader';
import GuideTour, { guideTargets, useGuideTour } from 'components/common/guide/GuideTour';
import OpinionSharingCard from 'components/social/feed/OpinionSharingCard';
import FeedIdentityCard from './FeedIdentityCard';
import YourWorkCard from 'components/social/feed/YourWorkCard';
import InterestsNewsCardStandalone from 'components/collab/interests/InterestsNewsCard';
import useFeedAPI from '../../../shared/hooks/useFeedAPI';
import { axiosService } from 'shared/helpers/axios';
import FollowButton from 'components/common/buttons/FollowButton';
import ConnectionsCard from 'components/social/connections/ConnectionsCard';
import PagedList from 'components/common/PagedList';
// import PrivacySettingsDialog from 'components/settings/PrivacySettingsDialog';
import EntityLink from 'components/common/links/EntityLink';
import { useUser } from 'shared/helpers/axios';
import { formatDistanceToNow } from 'date-fns';
import { useUserProfile } from 'shared/hooks/useUserProfile';
import PostItem from 'components/common/PostItem';

// The feed's guide, opened from the "?" next to the title. Each step points at the element marked
// data-guide="<target>" (a step whose element is not on the page shows without a spotlight).
const FEED_GUIDE_STEPS = [
  { label: 'You', title: 'Your profile at a glance',
    description: 'Your connections, what you follow and your consortia. Open your profile from here to keep it complete: it decides which calls and people you are matched with.',
    target: 'identity' },
  { label: 'Calls', title: 'Calls relevant to you',
    description: 'Open funding calls matched to the skills and research interests on your profile. "Show all" lists every match.',
    target: 'relevant-calls' },
  { label: 'Share', title: 'Post to your network',
    description: 'Share an update, a question or a result with your connections. Choose who can see it before posting.',
    target: 'composer' },
  { label: 'Announce', title: 'Make it an announcement',
    description: 'Switch a post to an Announcement for open positions, events, deadlines or releases: announcements also reach the Interests page for everyone.',
    target: 'announcement' },
  { label: 'Filter', title: 'Filter the activity feed',
    description: 'Narrow the feed by author (you, people you follow, others) or by activity type, and open archived items.',
    target: 'feed-filter' },
  { label: 'Interests', title: 'Interests and announcements',
    description: 'Researchers seeking a consortium or partners for a call, and the latest announcements. "Show all" opens the Interests page.',
    target: 'interests' },
  { label: 'Connections', title: 'Your connections',
    description: 'Connect with a researcher and, once they accept, you see their whole profile. Requests to connect with you wait here to be accepted or declined.',
    target: 'connections' },
  { label: 'Following', title: 'Organisations, calls and labs you follow',
    description: 'Their news and calls come to your feed. Discover & Follow below suggests more.',
    target: 'following' },
  { label: 'Invitations', title: 'Invitations waiting for you',
    description: 'Invitations to join consortia show here, ready to accept or decline.',
    target: 'invitations' },
];
const FEED_GUIDE_TARGETS = guideTargets(FEED_GUIDE_STEPS);

// the activity feed's side columns are at most this wide (px), and the feed in the middle at most FEED_MAX: posts
// (and their images) read best at a column's width; more room goes to the margins, the three columns centred
const SIDE_MAX = 300;
// one hover for every row in the feed's cards (calls, consortia, suggestions, invitations, connections…): light grey
const ROW_HOVER_SX = { borderRadius: 1.5, transition: 'background-color .15s', '&:hover': { bgcolor: 'action.hover' } };
// the number on a card's icon: an outlined bubble (connections, following)
const COUNT_BUBBLE_SX = { fontSize: '.8rem', height: 16, minWidth: 16, px: 0.5, transform: 'translate(50%, -60%)',
  top: 0, left: 0, bgcolor: '#fff', color: 'primary.main', border: '1px solid', borderColor: 'primary.light', fontWeight: 600 };
const FEED_MAX = 720;
const FEED_MIN = 540;          // a narrower feed: the right column goes under the left one
const COLUMN_GAP = 24;         // the grid's spacing={3}
// the columns that fit in the page's own width (not the window's: the sidebar takes part of it)
const columnsFor = (width) => (width >= 2 * SIDE_MAX + FEED_MIN + 2 * COLUMN_GAP ? 3
  : width >= SIDE_MAX + 400 + COLUMN_GAP ? 2 : 1);

// Rows shown in the compact Relevant Calls card before "Show All"
const RELEVANT_CALLS_SHOWN = 8;

// Sidebar card lists (calls, consortia, suggestions, invitations): theme sizes, tight lines
const COMPACT_LIST_SX = {
  '& .MuiTypography-body2, & .MuiTypography-caption, & .MuiTypography-subtitle2, & .MuiLink-root': { lineHeight: 1.35 },
};

// ---- Styled Components ----
const ProfileCard = styled(Card)(({ theme }) => ({
  ...commonStyles.feedProfile,
  marginBottom: theme.spacing(2),
  borderRadius: '16px',
  boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
}));

const SidebarCard = styled(Card)(({ theme }) => ({
  bgcolor: "#fff",
  marginBottom: theme.spacing(2),
  borderRadius: '12px',
  border: '1px solid rgba(0,0,0,0.08)',
  transition: 'all 0.2s ease',
  '&:hover': {
    boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
  },
}));

// The feed shows FEED_PAGE entries at a time; "More" (or scrolling to the end) shows the next ones, fetching
// further back (MAX_DEPTH times the usual number of posts at most) once everything loaded is shown
const FEED_PAGE = 20;
const MAX_DEPTH = 10;

function FeedMore({ onMore, loading, done }) {
  const ref = useRef(null);
  const onMoreRef = useRef(onMore);
  onMoreRef.current = onMore;
  useEffect(() => {
    const el = ref.current;
    if (!el || done || typeof IntersectionObserver === 'undefined') return undefined;
    const io = new IntersectionObserver(([e]) => { if (e.isIntersecting) onMoreRef.current(); }, { rootMargin: '300px' });
    io.observe(el);
    return () => io.disconnect();
  }, [done, loading]);
  if (done) {
    return <Typography variant="caption" color="text.disabled" textAlign="center" display="block" sx={{ py: 2 }}>You're all caught up</Typography>;
  }
  return (
    <Box ref={ref} textAlign="center" py={1.5}>
      <Button size="small" variant="outlined" onClick={onMore} disabled={loading} sx={{ borderRadius: 5, textTransform: 'none', bgcolor: 'background.paper' }}
        startIcon={loading ? <CircularProgress size={14} /> : <ExpandMoreIcon />}>
        {loading ? 'Loading older entries…' : 'More'}
      </Button>
    </Box>
  );
}

// Feed filter — author and type as multi-select arrays, persisted across reloads
const FILTERS_KEY = 'feed_filter_preferences_v2';
const DEFAULT_FILTERS = {
  authors: ['me', 'following', 'others'],
  types:   ['post', 'comment', 'consortium', 'suggestion', 'lab'],
  archived: false,
};

// ─────────────────────────────────────────────────────────────────────────────
// InterestsNewsCard: uses standalone component from collab/interests/
const InterestsNewsCard = InterestsNewsCardStandalone;

// ─────────────────────────────────────────────────────────────────────────────

const AlternativeProfileFeed = ({ profileId: profileIdProp }) => {
  const { profileId: profileIdParam } = useParams();
  const profileId = profileIdProp || profileIdParam;
  const navigate = useNavigate();
  
  const { user: currentUser } = useUser();
  const { user: userProfile } = useUserProfile(currentUser?.id); // Ensure user profile is loaded
  const theme = useTheme();

  // console.log('AlternativeProfileFeed rendering for profileId:', profileId, 'with userProfile:', userProfile);


  const {
    loading: feedLoading,
    userStats,
    createPost,
    refreshFeed,
    fetchConsortiumMembersPosts,
  } = useFeedAPI();


  const [loading, setLoading] = useState(true);
  // The feed comes from several requests (posts, followed activity, comments, consortia, suggestions…): it is shown
  // once the first round of them is back, all together, instead of entries popping in one source after another
  const [feedReady, setFeedReady] = useState(false);
  // const [user, setUser] = useState(null);
  const [error, setError] = useState(null);

  const [receivedSuggestions, setReceivedSuggestions] = useState([]);
  const [relevantCalls, setRelevantCalls] = useState([]);
  const [joinedConsortia, setJoinedConsortia] = useState([]);
  const [consortiumInvitations, setConsortiumInvitations] = useState([]);
  const [recentComments, setRecentComments] = useState([]);
  const [userPosts, setUserPosts] = useState([]);
  const [followedActivities, setFollowedActivities] = useState([]);
  // Recent posts of people you neither follow nor work with (author filter "Others")
  const [otherPosts, setOtherPosts] = useState([]);
  // How far back the feed goes: entries shown, and how deep the posts are fetched ("More" / scrolling down)
  const [visibleCount, setVisibleCount] = useState(FEED_PAGE);
  const depthRef = useRef(1);
  const [loadingMore, setLoadingMore] = useState(false);
  const [noOlder, setNoOlder] = useState(false);       // fetching deeper brought nothing new
  const [feedSearch, setFeedSearch] = useState('');
  const [following, setFollowing] = useState([]);
  const [suggestionsToFollow, setSuggestionsToFollow] = useState([]);
  const [loadingSuggestions, setLoadingSuggestions] = useState(true);
  const ARCHIVED_KEY = 'feed_archived_activity_ids';
  const [hiddenActivities, setHiddenActivities] = useState(() => {
    try {
      const saved = localStorage.getItem(ARCHIVED_KEY);
      return saved ? new Set(JSON.parse(saved)) : new Set();
    } catch { return new Set(); }
  });

  // Persist archived IDs across page reloads
  useEffect(() => {
    try {
      localStorage.setItem(ARCHIVED_KEY, JSON.stringify([...hiddenActivities]));
    } catch {}
  }, [hiddenActivities]);

  const handleRemoveActivity = (activityId) => {
    setHiddenActivities(prev => new Set([...prev, activityId]));
  };


  const [feedFilters, setFeedFilters] = useState(() => {
    try {
      const saved = localStorage.getItem(FILTERS_KEY);
      if (!saved) return DEFAULT_FILTERS;
      const parsed = JSON.parse(saved);
      // Types added after a preference was saved (e.g. 'lab') start switched on
      const knownTypes = Array.isArray(parsed.knownTypes) ? parsed.knownTypes : ['post', 'comment', 'consortium', 'suggestion'];
      const types = Array.isArray(parsed.types)
        ? [...parsed.types, ...DEFAULT_FILTERS.types.filter(t => !knownTypes.includes(t) && !parsed.types.includes(t))]
        : DEFAULT_FILTERS.types;
      return {
        authors:  Array.isArray(parsed.authors)  ? parsed.authors  : DEFAULT_FILTERS.authors,
        types,
        archived: typeof parsed.archived === 'boolean' ? parsed.archived : false,
      };
    } catch { return DEFAULT_FILTERS; }
  });

  useEffect(() => {
    try { localStorage.setItem(FILTERS_KEY, JSON.stringify({ ...feedFilters, knownTypes: DEFAULT_FILTERS.types })); } catch {}
  }, [feedFilters]);

  const toggleArchived = () => {
    setFeedFilters(prev => ({ ...prev, archived: !prev.archived }));
  };

  const [filterMenuAnchor, setFilterMenuAnchor] = useState(null);
  const [filterMenuKey, setFilterMenuKey] = useState(null);

  // const [privacyDialogOpen, setPrivacyDialogOpen] = useState(false);
  const [profileData, setProfileData] = useState({
    name: '',
    title: '',
    headline:'',
    organization: '',
    location: '',
    email: '',
    bio: '',
    stats: { collaborations: 0, publications: 0, citations: 0, projects: 0, h_index: 0, funding_amount: 0 },
    expertise: [],
    badges: [],
  });




  
  const fetchUserProfile = async () => {
    setLoading(true);
    setError(null);
    try {
      // Profile data is loaded via useUserProfile hook.
      // Subcomponent data is fetched once userProfile.id is available.
      // console.log('Profile loading initiated');
    } catch (e) {
      // console.error('Error fetching user profile:', e);
      setError('Error loading profile data');
    } finally {
      setLoading(false);
    }
  };

  const fetchConsortiaMemberships = async (userId) => {
    try {
      const res = await axiosService.get(`/consortium/memberships/by-user/?user_id=${userId}`);
      setJoinedConsortia(res.data?.results || res.data || []);
    } catch (e) {
      // console.error('Error fetching consortia memberships:', e);
      setJoinedConsortia([]);
    }
  };

  const fetchReceivedSuggestions = async () => {
    try {
      const res = await axiosService.get('/interact/suggestions/', { params: { recipient: 'me' } });   // only those sent to you
      const filteredSuggestions = res.data.results.filter ( (s) => { 
        

          // console.log('Checking suggestion recipient:', s.recipient_info.id, 'against current user:', currentUser?.id); 
          return s.recipient_info.id === currentUser?.id;      

          }
        )   
      

      setReceivedSuggestions(  filteredSuggestions || []);
    } catch (e) {
      // console.error('Error fetching received suggestions:', e);
      setReceivedSuggestions([]);
    }
  };

  // The call list is slow to load (~250 calls), and this runs on every profile update. Load it
  // once and share the request; only the matching is redone, and only the latest run may set
  // the list, so a run that started before the profile loaded can't overwrite the matches.
  const relevantCallsRequest = useRef(0);
  const openCallsPromise = useRef(null);
  const fetchRelevantCalls = async ({ reload = false } = {}) => {
    if (!userProfile) return;
    const requestId = ++relevantCallsRequest.current;
    try {

      // Get user's skills and research interests
      const userSkills = userProfile?.skills || [];
      const userInterests = userProfile?.research_interests || [];

      // Get skill IDs from user skills
      const userSkillIds = userSkills.map(s => s.skill?.id).filter(Boolean);
      
      // Get research category codes and names for matching with call categories
      const userInterestAreaCodes = userInterests.map(i => i.category?.code).filter(Boolean);
      const userInterestAreaNames = userInterests.map(i => i.category?.name).filter(Boolean);

      // console.log('User research interests:', userInterests);
      // console.log('User interest codes:', userInterestAreaCodes);
      // console.log('User interest names:', userInterestAreaNames);
      // console.log('User skill IDs:', userSkillIds);

      // Fetch recent active calls (once, shared by concurrent callers)
      if (reload || !openCallsPromise.current) {
        openCallsPromise.current = axiosService.get('/relevant-calls/', {
          params: { page_size: 250, ordering: '-created' },
        });
        openCallsPromise.current.catch(() => { openCallsPromise.current = null; });   // retry next time
      }
      const relevantCalls = await openCallsPromise.current;

      // console.log('Fetched calls for relevance matching:', relevantCalls?.data?.results || relevantCalls?.data);

      const allCalls = relevantCalls?.data?.results || relevantCalls?.data || [];

      const now = new Date();
      const calls = allCalls.filter(call =>
        call.is_always_open || !call.closes_at || new Date(call.closes_at) > now
      );

      // Debug: Check first call structure
      // if (calls.length > 0) {
      //   console.log('First call sample:', {
      //     code: calls[0].code,
      //     categories: calls[0].categories,
      //     skills: calls[0].skills,
      //     categoriesType: typeof calls[0].categories,
      //     skillsType: typeof calls[0].skills,
      //   });
      // }

      // Calculate match score for each call
      const callsWithScores = calls.map(call => {
        let score = 0;
        const matchingSkills = [];
        const matchingInterests = [];
        
        // Match user skills with call skills (both use Skill entity)
        const callSkills = call?.skills || [];
        callSkills.forEach(cs => {
          const skillId = cs?.id;
          if (skillId && userSkillIds.includes(skillId)) {
            matchingSkills.push(cs);
            score += 1; // Weight skills higher
          }
        });

        // Match user research interests with call categories (by code and name)
        const callCategories = call?.categories || [];
        
        callCategories.forEach(cc => {
          // call.categories is a flat array of category objects from the backend
          const categoryCode = cc?.code;
          const categoryName = cc?.name;
          
          // Match by code (more precise)
          if (categoryCode && userInterestAreaCodes.includes(categoryCode)) {
            matchingInterests.push(cc);
            score += 1;
          }
          // Match by name (fallback if code doesn't match)
          else if (categoryName && userInterestAreaNames.includes(categoryName)) {
            matchingInterests.push(cc);
            score += 1;
          }
        });

        // Debug logging for calls with any score
        if (score > 0) {
          // console.log(`Call ${call.code}: ${matchingSkills.length} skill matches, ${matchingInterests.length} interest matches, score: ${score}`);
        }

        // console.log(`Call ${call.code} - Matching Skills:`, matchingSkills);

        return { 
          ...call, 
          matchScore: score, 
          skillMatches: matchingSkills.length, 
          interestMatches: matchingInterests.length,
          matchingSkills,
          matchingInterests,
        };
      });

      // Filter and sort by match score
      const relevantCallsList = callsWithScores
        .filter(call => call.matchScore > 0)

        .sort((a, b) => b.matchScore - a.matchScore)
        // .slice(0, 5); // Top 5 relevant calls

      if (requestId === relevantCallsRequest.current) setRelevantCalls(relevantCallsList);
    } catch (e) {
      // console.error('Error fetching relevant calls:', e);
      if (requestId === relevantCallsRequest.current) setRelevantCalls([]);
    }
  };




  const fetchRecentComments = async (userId) => {
    try {
      const res = await axiosService.get(`/interact/comments/?author=${userId}&page_size=10`);
      setRecentComments(res.data?.results || res.data || []);
    } catch (e) {
      // console.error('Error fetching recent comments:', e);
      setRecentComments([]);
    }
  };

  const fetchConsortiumInvitations = async () => {
    try {
      const res = await axiosService.get('/interact/consortium-invitations/received/');
      setConsortiumInvitations(res.data?.results || res.data || []);
    } catch (e) {
      // console.error('Error fetching consortium invitations:', e);
      setConsortiumInvitations([]);
    }
  };

  const fetchOtherPosts = async () => {
    try {
      const res = await axiosService.get(`/interact/posts/?page_size=${Math.min(200, 40 * depthRef.current)}`);
      const rows = res.data?.results || res.data || [];
      setOtherPosts(rows);
      return rows;
    } catch (e) {
      setOtherPosts([]);
    }
  };

  // A repost (or any new post made outside the composer) shows up in the feed at once
  useEffect(() => {
    const refresh = () => { if (userProfile?.id) fetchUserPosts(userProfile.id); };
    window.addEventListener('postCreated', refresh);
    return () => window.removeEventListener('postCreated', refresh);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userProfile?.id]);

  const fetchUserPosts = async (userId) => {
    try {
      const res = await axiosService.get(`/interact/posts/?author=${userId}&page_size=${Math.min(200, 10 * depthRef.current)}`);
      const rows = res.data?.results || res.data || [];
      setUserPosts(rows);
      return rows;
    } catch (e) {
      // console.error('Error fetching user posts:', e);
      setUserPosts([]);
    }
  };

  const fetchFollowedActivities = async () => {
    try {
      // First get what the user is following
      const followsRes = await axiosService.get('/interact/follows/following/');
      const follows = followsRes.data?.results || followsRes.data || [];
      
      // Update following state
      setFollowing(follows);

      // Build all fetch promises in parallel instead of sequential loop
      const userFollows = follows.filter(f => f.content_type === 'user');
      const consortiumFollows = follows.filter(f => f.content_type === 'consortium');

      // the people you follow: their latest posts (at most 5 each) in one request, not one per person
      const followById = Object.fromEntries(userFollows.map(f => [String(f.object_id), f]));
      const userPostsPromise = userFollows.length
        ? axiosService.get(`/interact/posts/?authors=${userFollows.map(f => f.object_id).join(',')}&page_size=${Math.min(200, userFollows.length * 5 * depthRef.current)}`)
          .then(res => {
            const perAuthor = {};
            return (res.data?.results || res.data || []).filter(post => {
              const a = String(post.author?.id || post.author);
              perAuthor[a] = (perAuthor[a] || 0) + 1;
              return perAuthor[a] <= 5 * depthRef.current;
            }).map(post => ({
              ...post,
              activity_type: 'followed_post',
              followed_entity: followById[String(post.author?.id || post.author)]?.followed_object,
              followed_entity_type: 'user',
            }));
          })
          .catch(() => [])
        : Promise.resolve([]);

      const consortiumActivities = consortiumFollows.map(follow => ({
        id: `followed-consortium-${follow.object_id}`,
        activity_type: 'followed_consortium_update',
        followed_entity: follow.followed_object,
        followed_entity_type: 'consortium',
        created: formatDistanceToNow(new Date(follow.created), { addSuffix: true }), // Assuming follow.created is the timestamp of the follow action
        text: `You started following "${follow?.followed_object?.name || 'a consortium'}"`,
      }));

      const consortiumPostsPromise = fetchConsortiumMembersPosts(userProfile?.id)
        .then(posts => posts.map(post => ({
          ...post,
          activity_type: 'followed_post',
          followed_entity_type: 'consortium_member',
        })))
        .catch(() => []);

      // Updates from followed labs: news, open positions, new projects, new members
      const labUpdatesPromise = follows.some(f => f.content_type === 'lab')
        ? axiosService.get('/consortium/labs/feed/').then(res => (res.data || []).map(u => ({
            ...u, activity_type: 'followed_lab_update',
          }))).catch(() => [])
        : Promise.resolve([]);

      // Run all in parallel
      const [userPostResults, consortiumMemberPosts, labUpdates] = await Promise.all([
        userPostsPromise,
        consortiumPostsPromise,
        labUpdatesPromise,
      ]);

      const followedActivities = [
        ...userPostResults,
        ...consortiumActivities,
        ...consortiumMemberPosts,
        ...labUpdates,
      ];

      setFollowedActivities(followedActivities);
      return followedActivities;
    } catch (e) {
      // console.error('Error fetching followed activities:', e);
      setFollowedActivities([]);
    }
  };

  // Discover & Follow: drawn by the server in a random order (a new seed draws afresh), five at a time with no
  // repeats; the next five come when the card reaches its last page (GET /interact/follows/discover/)
  // WANTED: the card asked for more while a draw was loading: asked again once it is done
  const discover = useRef({ seed: 0, page: 0, more: true, busy: false, wanted: false });
  const loadMoreRef = useRef(null);
  const fetchSuggestionsToFollowData = useCallback(async () => {
    discover.current = { seed: Math.floor(Math.random() * 1e9), page: 0, more: true, busy: true, wanted: false };
    setLoadingSuggestions(true);
    try {
      const res = await axiosService.get('/interact/follows/discover/', { params: { seed: discover.current.seed, page: 0 } });
      setSuggestionsToFollow(res.data?.results || []);
      discover.current.more = Boolean(res.data?.has_more);
    } catch (e) {
      setSuggestionsToFollow([]);
    } finally {
      discover.current.busy = false;
      setLoadingSuggestions(false);
      if (discover.current.wanted) loadMoreRef.current?.();
    }
  }, []);
  const loadMoreSuggestions = useCallback(async () => {
    const d = discover.current;
    if (d.busy) { d.wanted = true; return; }
    if (!d.more) return;
    d.busy = true;
    d.wanted = false;
    try {
      const res = await axiosService.get('/interact/follows/discover/', { params: { seed: d.seed, page: d.page + 1 } });
      d.page += 1;
      d.more = Boolean(res.data?.has_more);
      setSuggestionsToFollow((list) => [...list, ...(res.data?.results || [])]);
    } catch (e) {
      d.more = false;
    } finally {
      d.busy = false;
      if (d.wanted) loadMoreRef.current?.();
    }
  }, []);
  loadMoreRef.current = loadMoreSuggestions;

  const suggestionsBuilder = useRef(fetchSuggestionsToFollowData);
  suggestionsBuilder.current = fetchSuggestionsToFollowData;

  useEffect(() => {
    if (userProfile) fetchSuggestionsToFollowData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [Boolean(userProfile)]);

  // The feed's own sources are awaited (the list shows when they are all back); what only the side cards use
  // (relevant calls, the profile stats) loads alongside without holding the feed back. The following list comes
  // with the followed activities (one request, not two); recent likes were fetched but never shown.
  const handleRefresh = async () => {
    try {
      refreshFeed();
      if (userProfile?.id) fetchRelevantCalls({ reload: true });
      const fetches = userProfile?.id ? [
        fetchConsortiaMemberships(userProfile.id),
        fetchReceivedSuggestions(),
        fetchRecentComments(userProfile.id),
        fetchConsortiumInvitations(),
        fetchUserPosts(userProfile.id),
        fetchFollowedActivities(),
        fetchOtherPosts(),
      ] : [];

      await Promise.all(fetches);
      // the latest builder: this routine may hold one from before the lists arrived (it gave "No suggestions")
      suggestionsBuilder.current();
    } catch (e) {
      // console.error('Error refreshing:', e);
    }
  };

  const handleCreatePost = async (postData) => {
    try {
      const created = await createPost({
        title: postData.title || '',
        body: postData.content,
        visibility: postData.visibility || 'otable',
        is_announcement: postData.is_announcement || false,
        link_preview: postData.link_preview || null,
      });
      // Refresh the posts after creating
      if (userProfile) {
        await fetchUserPosts(userProfile.id);
      }
      return created;                    // the composer links a reflection made from it
    } catch (error) {
      console.error('Error creating post:', error);
      throw error;
    }
  };





  // Load profile & feed
  useEffect(() => {
    fetchUserProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profileId]);

  // Once userProfile is available, fetch all subcomponent data in one pass
  useEffect(() => {
    if (userProfile?.id) {
      handleRefresh().finally(() => setFeedReady(true));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userProfile?.id]);


  useEffect(() => {
    if (userProfile) {
      setProfileData({
        name: userProfile?.full_name || `${userProfile.first_name || ''} ${userProfile.last_name || ''}`.trim(),
        title: userProfile.position || '',
        organization: (typeof userProfile.organization === 'string' ? userProfile.organization : userProfile.organization?.name) || '',
        location: userProfile.location || '',
        headline: userProfile.headline || '',
        email: userProfile.email || '',
        bio: userProfile.bio || '',
        stats: {
          collaborations: userStats.collaborationsCount || userProfile.collaborations_count || 0,
          publications: userProfile.publications_count || 0,
          citations: userProfile.citations_count || 0,
          projects: userProfile.active_projects_count || 0,
          h_index: userProfile.h_index || 0,
          funding_amount: userProfile.funding_amount || 0,
        },
        expertise: userProfile.expertise || [],
        badges: userProfile.badges || [],
      });
    }
  }, [userProfile, userStats]);


  useEffect( () => {
     fetchReceivedSuggestions();
     fetchRelevantCalls();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser?.id, userProfile]);

  // scrolls the step's element into view, then the tour measures it
  const guide = useGuideTour({ storageKey: 'feed.guide.dismissed.v1', steps: FEED_GUIDE_STEPS,
    targetRefs: FEED_GUIDE_TARGETS, autoOpen: false, recalcDeps: [loading, feedLoading] });
  useEffect(() => {
    if (!guide.open) return;
    FEED_GUIDE_TARGETS[FEED_GUIDE_STEPS[guide.step]?.target]?.current?.scrollIntoView({ block: 'center', behavior: 'smooth' });
  }, [guide.open, guide.step]);

  // How many columns fit, from the grid's own width (watched as the window or the sidebar changes). With two, the
  // right column's cards go under the left column's, not to the end of the page after the whole feed
  const [columns, setColumns] = useState(() => columnsFor((typeof window !== 'undefined' ? window.innerWidth : 1400) - 300));
  const gridObserver = useRef(null);
  const gridRef = useCallback((node) => {
    gridObserver.current?.disconnect();
    if (!node) return;
    const measure = () => setColumns(columnsFor(node.parentElement?.clientWidth || node.clientWidth));
    measure();
    gridObserver.current = new ResizeObserver(measure);
    gridObserver.current.observe(node.parentElement || node);
  }, []);
  useEffect(() => () => gridObserver.current?.disconnect(), []);
  const midWidth = columns === 2;
  // the columns' widths follow the count, not the window's breakpoints (no xs size beside them: it would win)
  const leftColSx = columns === 1 ? {} : { flex: `0 0 ${SIDE_MAX}px`, maxWidth: SIDE_MAX };
  const feedColSx = columns === 1 ? {} : { flex: '1 1 0', minWidth: 0, maxWidth: FEED_MAX };
  const rightColSx = columns === 3 ? { flex: `0 0 ${SIDE_MAX}px`, maxWidth: SIDE_MAX } : {};
  // the Following card: what you follow other than people (people are your connections)
  const followedEntities = following.filter((f) => (f.content_type || '').toLowerCase() !== 'user');
  const rightColumnItems = (
    <Stack spacing={2}>


    {/* Interests News Card */}
      <Box data-guide="interests"><InterestsNewsCard /></Box>

      {/* Connections: requests to connect with you, and your connections */}
      <SidebarCard data-guide="connections"><ConnectionsCard /></SidebarCard>

      {/* Following: organisations, consortia, calls, labs… (people are your connections, above) */}
      <SidebarCard data-guide="following">
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Badge badgeContent={followedEntities.length}  sx={{ '& .MuiBadge-badge': { ...COUNT_BUBBLE_SX } }}>
                <PersonIcon sx={{ color: 'primary.light', fontSize: '1.45rem' }} />
              </Badge>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>Following</Typography>
            </Box>
            <Button
              size="small"
              variant="text"
              onClick={() => navigate('/social/followed/')}
              sx={{ fontSize: '0.9rem', whiteSpace: 'nowrap' }}
            >
              See All
            </Button>
          </Box>
          {followedEntities.length === 0 ? (
            <Typography variant="body2" color="text.secondary">Not following any organisations, calls or labs yet</Typography>
          ) : (
            // five a page, sliding (components/common/PagedList.jsx)
            <PagedList items={followedEntities} keyOf={(f) => f.id ?? `${f.content_type}:${f.object_id}`} label="followed"
              renderItem={(follow) => {
                const contentType = follow.content_type?.toLowerCase() || '';
                // console.log("*****followed_object:", follow);
                const entityName = follow.followed_object?.name ||
                                  follow.followed_object?.title ||
                                  `Unknown ${follow.content_type}`;
                
                // console.log("**** followed object:", follow);
                const entityAvatar = follow.followed_object?.avatar || 
                                      follow.followed_object?.logo ||
                                      follow.followed_object?.image_url ;

                const entityType = follow.followed_object?.type || '';
                const entityDescription = (() => {
                  switch (entityType) {
                    case 'user':         return 'Researcher';
                    case 'organization': return 'Organization';
                    case 'consortium':   return 'Consortium';
                    case 'call':         return 'Funding Call';
                    case 'agency':       return 'Funding Agency';
                    case 'program':      return 'Funding Program' || follow.followed_object?.name ;
                    default:             return contentType || '';
                  }
                })();


                // console.log("**** follow.followed_object:", follow.followed_object);

                // const handleClick = () => {
                //   if (isUser) {
                //     navigate(`/profile/${follow.object_id}`);
                //   } else if (isOrganization) {
                //     navigate(`/organizations/${follow.object_id}`);
                //   } else if (isConsortium) {
                //     navigate(`/consortium/consortiums/${follow.object_id}`);
                //   } else if (isCall) {
                //     navigate(`/funding/calls/${follow.object_id}`);
                //   } else if (isAgency) {
                //     navigate(`/funding/agencies/${follow.object_id}`);
                //   } else if (isProgram) {
                //     navigate(`/funding/programs/${follow.object_id}`);
                //   }
                // };
                
                return (
                  <Box display="flex" alignItems="center" justifyContent="space-between" gap={1} sx={{ px: 0.5, py: 0.25, ...ROW_HOVER_SX }}>
                    <EntityLink
                      type={contentType}
                      id={follow.object_id}
                      name={entityName}
                      description={entityDescription}
                      showAvatar={true}
                      variant="body2"
                      color={theme.palette.text.primary}
                      underline="none"
                      avatar={{
                        src: entityAvatar,
                        alt: entityName?.charAt(0)?.toUpperCase(),
                        children: entityName?.charAt(0)?.toUpperCase(),
                        sx: {
                          backgroundColor: theme.palette.primary.avatar2,
                          color: 'white',
                          boxShadow: '0 2px 6px rgba(0,0,0,0.2)',
                        },
                      }}
                    />
                    <FollowButton
                      contentType={follow.content_type}
                      objectId={follow.object_id}
                      size="small"
                      variant="outlined"
                      compact
                    />
                  </Box>
                );
              }} />
          )}
        </CardContent>
      </SidebarCard> 
        
    {/* Suggestions to Follow */}
      <SidebarCard>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center' }}>
              <DiscoverIcon sx={{ mr: 1, color: 'primary.light' }} />
              Discover & Follow
            </Typography>
          </Box>
          {loadingSuggestions ? (
            <Box display="flex" flexDirection="column" alignItems="center" py={2}>
              <LinearProgress sx={{ width: '100%', mb: 1 }} />
              <Typography variant="body2" color="text.secondary">Loading suggestions...</Typography>
            </Box>
          ) : suggestionsToFollow.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No suggestions available at the moment.
            </Typography>
          ) : (
            <PagedList items={suggestionsToFollow} keyOf={(x, i) => x.id ?? i} spacing={1} label="suggestions" onEnd={loadMoreSuggestions}
              renderItem={(suggestion) => (
                <Box display="flex" alignItems="center" justifyContent="space-between" gap={1} sx={{ px: 0.5, py: 0.25, ...ROW_HOVER_SX }}>
                  <EntityLink
                    type={suggestion.content_type}
                    id={suggestion.object_id}
                    name={suggestion.name}
                    description={
                      suggestion.content_type === 'fundingprogram'
                        ? 'Funding program' : suggestion.description
                    }
                    showAvatar={true}
                    variant="body2"
                    color="text.primary"
                    underline="none"
                    avatar={{
                      src: suggestion.avatar,
                      alt: suggestion.name?.charAt(0) || suggestion.type?.charAt(0),
                      children: suggestion.name?.charAt(0) || suggestion.type?.charAt(0),
                      sx: {
                        backgroundColor: theme.palette.primary.avatar2,
                        color: theme.palette.primary.contrastText,
                      },
                    }}
                  />
                  <FollowButton
                    contentType={suggestion.content_type}
                    objectId={suggestion.object_id}
                    size="small"
                    variant="outlined"
                    compact
                    compactAll
                  />
                </Box>
              )} />
          )}

        
        </CardContent>
      </SidebarCard>
  
    
    </Stack>
  );

  return (
    // as wide as the three columns (and the gaps between them): the title stays over the left column
    <PageContainer width="wide" sx={{ maxWidth: { sm: SIDE_MAX * 2 + FEED_MAX + 3 * 24 + 2 * 24 } }}>
      <PageHeader title="Activity Feed">
        <Tooltip title="Show Activity Feed guide" placement="bottom">
          <IconButton onClick={guide.show} size="small" aria-label="Show Activity Feed guide">
            <HelpOutlineIcon sx={{ fontSize: '1.25rem' }} />
          </IconButton>
        </Tooltip>
      </PageHeader>

      {loading ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px" flexDirection="column">
          <LinearProgress sx={{ width: '100%', mb: 2, maxWidth: 400 }} />
          <Typography variant="body2" color="text.secondary">Loading your feed…</Typography>
        </Box>
      ) : error ? (
        <ProfileCard>
          <CardContent sx={{ textAlign: 'center', py: 8 }}>
            <Typography variant="h6" color="error" gutterBottom>Error Loading Profile</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>{error}</Typography>
            <Button onClick={handleRefresh} variant="contained" color="primary">Try Again</Button>
          </CardContent>
        </ProfileCard>
      ) : !userProfile ? (
        <ProfileCard>
          <CardContent sx={{ textAlign: 'center', py: 8 }}>
            <Typography variant="h6" color="text.secondary" gutterBottom>Profile not found</Typography>
            <Typography variant="body2" color="text.secondary">The requested profile could not be loaded.</Typography>
          </CardContent>
        </ProfileCard>
      ) : (
        <Grid container ref={gridRef} columns={16} spacing={3} alignItems="flex-start">
          {/* Left Column - Profile Info + Consortia & Suggestions */}
          {/* the side columns stop at SIDE_MAX; the feed in the middle takes the rest */}
          <Grid item xs={columns === 1 ? 16 : undefined} sx={leftColSx}>
            <Stack spacing={2}>
              {/* Profile Info Card */}
              <Box data-guide="identity"><FeedIdentityCard
                user={userProfile}
                name={profileData.name}
                headline={profileData.headline || profileData.title}
                onOpenProfile={() => userProfile?.id && navigate(`/profile/${userProfile.id}/`)}
              /></Box>



              {/* Your work: what is waiting for you, only when something is (no card otherwise) */}
              <YourWorkCard />

              {/* Relevant Calls Card */}
              <SidebarCard data-guide="relevant-calls">
                <CardContent>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, display: 'flex', alignItems: 'center' }}>
                    <CampaignIcon sx={{ mr: 1, color: 'primary.main' }} />
                    Relevant Calls ({relevantCalls.length})
                  </Typography>
                  {relevantCalls.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                      No calls match your skills and interests
                    </Typography>
                  ) : (
                    <Stack spacing={0} sx={COMPACT_LIST_SX}>
                      {relevantCalls.slice(0, RELEVANT_CALLS_SHOWN).map((call) => (
                        <Tooltip
                          key={call.id}
                          placement="right"
                          arrow
                          enterDelay={250}
                          title={(
                            <Box sx={{ maxWidth: 320 }}>
                              <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.35 }}>
                                {call.title || call.name || call.code}
                              </Typography>
                              {call.code && call.title && (
                                <Typography variant="caption" sx={{ opacity: 0.8 }}>{call.code}</Typography>
                              )}
                            </Box>
                          )}
                        >
                        <Box
                          onClick={() => navigate(`/funding/call/${call.id}`)}
                          sx={{
                            cursor: 'pointer', px: 2, py: 0.75, ...ROW_HOVER_SX,
                          }}
                        >
                          {/* One compact line for the code and match chips, one for agency and deadline */}
                          <Box display="flex" alignItems="center" gap={0.5} minWidth={0}>
                            <Typography
                              variant="body2" noWrap
                              sx={{ fontWeight: 500, lineHeight: 1.35, flex: 1, minWidth: 0 }}
                            >
                              {call.code || call.title || call.name || 'Untitled Call'}
                            </Typography>
                            {call.skillMatches > 0 && (
                              <Chip
                                label={`${call.skillMatches} skill${call.skillMatches !== 1 ? 's' : ''}`}
                                size="small" color="primary"
                                sx={{ height: 18, fontSize: '0.75rem', '& .MuiChip-label': { px: 0.75 } }}
                              />
                            )}
                            {call.interestMatches > 0 && (
                              <Chip
                                label={`${call.interestMatches} interest${call.interestMatches !== 1 ? 's' : ''}`}
                                size="small" color="secondary"
                                sx={{ height: 18, fontSize: '0.75rem', color: '#fff', '& .MuiChip-label': { px: 0.75 } }}
                              />
                            )}
                          </Box>
                          {(() => {
                            const deadline = call.deadline || call.closes_at;
                            const meta = [
                              call.agency_name,
                              call.is_always_open ? 'Always open' : deadline && `Deadline ${new Date(deadline).toLocaleDateString()}`,
                            ].filter(Boolean).join(' • ');
                            return meta && (
                              <Typography variant="caption" color="text.secondary" noWrap component="div" sx={{ lineHeight: 1.35 }}>
                                {meta}
                              </Typography>
                            );
                          })()}
                        </Box>
                        </Tooltip>
                      ))}
                      {relevantCalls.length > RELEVANT_CALLS_SHOWN && (
                        <Button 
                          size="small" 
                          variant="text" 
                          sx={{ alignSelf: 'flex-start', px: 2, mt: 0.5 }} 
                          onClick={() => navigate(`/feed/${profileId}/relevant-calls`)}
                        >
                          Show All ({relevantCalls.length})
                        </Button>
                      )}
                    </Stack>
                  )}
                </CardContent>
              </SidebarCard>

              {/* Consortia Card */}
              <SidebarCard>
                <CardContent>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, display: 'flex', alignItems: 'center' }}>
                    <Groups sx={{ mr: 1, color: 'primary.main' }} />
                     Consortium ({joinedConsortia.length})
                  </Typography>
                  {joinedConsortia.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">Not a member of any consortia</Typography>
                  ) : (
                    <Stack spacing={0} sx={COMPACT_LIST_SX}>
                      {joinedConsortia.slice(0, 4).map((membership, i) => (
                        // console.log('Rendering consortium membership:', membership) ||
                        <>
                        <Box key={i} sx={{  cursor: 'pointer', px:2, py:0.75, ...ROW_HOVER_SX }} 
                            onClick={() => navigate(`/consortium/${membership?.consortium.id}`)}
                            
                            >
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {membership?.consortium.name || 'Consortium'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Role: {membership.role?.label || 'Member'} • Joined {formatDistanceToNow(new Date(membership.created), { addSuffix: true })}
                          </Typography>
                        </Box>
{/* 
<Divider sx={{ borderColor: 'primary.main', mx:2 }} /> */}
</>
                      ))}
                      {joinedConsortia.length > 4 && (
                        <Button size="small" variant="text" sx={{ alignSelf: 'flex-start', px: 2, mt:1 }} onClick={() => navigate('/consortium/')}>
                          View all ({joinedConsortia.length})
                        </Button>
                      )}
                    </Stack>
                  )}
                </CardContent>
              </SidebarCard>



              {/* Suggestions Card */}
              <SidebarCard>
                <CardContent >
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, display: 'flex', alignItems: 'center' }}>
                    <SendIcon sx={{ mr: 1, color: 'primary.light' }} />
                    Received Suggestions
                  </Typography>
                  {receivedSuggestions.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">No recent suggestions</Typography>
                  ) : (
                    


                    <Stack spacing={0} sx={COMPACT_LIST_SX}>
                      { receivedSuggestions?.slice(0, 4).map((suggestion, i) => (
                     

                        <>
<Box 
  key={i}
  sx={{
    cursor: 'pointer',
    px: 2,
    py: 0.75,
    // border: '1px solid',
    // borderColor: 'divider',
    borderRadius: 1,
    // bgcolor: 'background.paper',
    position: 'relative',
    ...ROW_HOVER_SX,
  }}
  onClick={() => navigate('/suggestions/')}
>
  {/* Compact layout */}
  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
    {/* Avatar */}
    {/* <Avatar 
      src={suggestion?.author_info?.avatar_url}
      sx={{ width: 32, height: 32, mt: 0.5 }}
    >
      {suggestion?.author_info?.name?.charAt(0) || '?'}
    </Avatar> */}
    
    {/* Content */}
    
    <Box sx={{ flex: 1 }}>
      {/* First line: Author and time */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.25 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: .5 }}>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {suggestion?.author_info?.name}
        </Typography>

        <Typography variant="body2" color="text.secondary">
           suggested:
        </Typography>
        </Box>

      </Box>
      
      {/* Second line: Target info */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
     
        {/* <Typography variant="body2" sx={{ fontWeight: 500 }}> */}
          {/* 
          

        {/* </Typography> */}

                              <EntityLink
                                        type={suggestion.target_type}
                                        id={suggestion.target_id}
                                        name={(suggestion?.target_name?.slice(0, 20) || '') + (suggestion?.target_name?.length > 20 ? '...' : '')}
                                        variant="body2"
                                        color={theme.palette.text.primary}
                                        underline="none"
                                        sx={{ fontWeight: 500, '&:hover': { color: 'primary.main' } }}
                                  />


                            <Typography variant="caption" color="text.secondary" sx={{ position: 'absolute', bottom: 8, right: 12 }}>
                              {formatDistanceToNow(new Date(suggestion.created), { addSuffix: true })}
                            </Typography>

      </Box>
      
      {/* Third line: Recipient if available */}
      {suggestion?.recipient_name && (
        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
          To: {suggestion?.recipient_name}
        </Typography>
      )}
    </Box>

  
  </Box>

 

</Box>

{/* <Divider sx={{ borderColor: 'primary.main', mx:2 }} /> */}
                        
                     </>
                     ))}

                      {receivedSuggestions.length > 4 && (
                        <Button size="small" variant="text" sx={{ alignSelf: 'flex-start', px: 2, mt: 1 }} onClick={() => navigate('/suggestions/')}>
                          View all ({receivedSuggestions.length})
                        </Button>
                      )}
                    </Stack>
                  )}
                </CardContent>
              </SidebarCard>

              {/* Invitations */}
              <SidebarCard data-guide="invitations">
                <CardContent>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, display: 'flex', alignItems: 'center' }}>
                    <GroupAddIcon sx={{ mr: 1, color: 'primary.light' }} />
                    Invitations
                  </Typography>
                  {consortiumInvitations.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">No pending invitations</Typography>
                  ) : (
                    <Stack spacing={0} sx={COMPACT_LIST_SX}>
                      {consortiumInvitations.slice(0, 4).map((invitation, i) => (



<>
<Box 
  key={i}
  sx={{
    cursor: 'pointer',
    px: 2,
    py: 0.75,
    // border: '1px solid',
    // borderColor: 'divider',
    borderRadius: 1,
    bgcolor: 'background.paper',
    position: 'relative',
    ...ROW_HOVER_SX,
  }}
  onClick={() => navigate(`/consortium/${invitation.consortium?.id}`)}
>
  {/* Compact layout */}
  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
    {/* Avatar */}
    {/* <Avatar 
      src={suggestion?.author_info?.avatar_url}
      sx={{ width: 32, height: 32, mt: 0.5 }}
    >
      {suggestion?.author_info?.name?.charAt(0) || '?'}
    </Avatar> */}
    
    {/* Content */}
    
    <Box sx={{ flex: 1 }}>
      {/* First line: Author and time */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.25 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: .5 }}>
       

          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                {invitation.invitation?.from_user?.first_name} {invitation.invitation?.from_user?.last_name}
            </Typography>

            <Typography variant="body2" >
                       invited you to
          </Typography>

            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {invitation.consortium?.name || 'Consortium'}
          </Typography>
        
         

        </Box>
        <Typography variant="caption" color="text.secondary" sx={{ position: 'absolute', bottom: 8, right: 20 }}>
          {formatDistanceToNow(new Date(invitation.created), { addSuffix: true })}
        </Typography>
      </Box>
      
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                            Status: {invitation.invitation?.status || 'Pending'}
             </Typography>
  

    </Box>

  
  </Box>


</Box>

{/* <Divider sx={{ borderColor: 'primary.main', mx:2 }} /> */}
                        
                     </>



                      ))}
                      {consortiumInvitations.length > 4 && (
                        <Button size="small" variant="text" sx={{ alignSelf: 'flex-start' }}>
                          View all ({consortiumInvitations.length})
                        </Button>
                      )}
                    </Stack>
                  )}
                </CardContent>
              </SidebarCard>




              {midWidth && rightColumnItems}
            </Stack>
          </Grid>

          {/* Middle Column - Post Box + Activity Feed */}
          <Grid item xs={columns === 1 ? 16 : undefined} sx={feedColSx}>
            {/* the composer close to the feed: the posts are what you come for */}
            <Stack spacing={1}>
              {/* Post Box */}
              {/* <OpinionCard> */}
                {/* <CardContent sx={{ p: 0 }}> */}
                  <Box data-guide="composer">
                    
                  <Box data-guide="feed-filter" sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                    <TrendingUpIcon sx={{ color: 'primary.main' }} aria-hidden />
                    <TextField size="small" placeholder="Search in activity feed..." value={feedSearch}
                      onChange={(e) => { setFeedSearch(e.target.value); setVisibleCount(FEED_PAGE); }}
                      sx={{ mr: 'auto', width: { xs: '100%', sm: 220 }, '& .MuiOutlinedInput-root': { 
                            bgcolor: 'background.paper', borderRadius: 5, height: 32, fontSize: '1rem' },
                        // hovered or focused, the outline stays as it is: the blinking cursor is enough
                        '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline, & .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline':
                          { borderColor: 'rgba(0, 0, 0, 0.23)', borderWidth: '1px' } }}
                      InputProps={{
                        startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 18 }} /></InputAdornment>,
                        ...(feedSearch && { endAdornment: (
                          <InputAdornment position="end">
                            <IconButton size="small" aria-label="Clear the search" onClick={() => setFeedSearch('')}><CloseIcon sx={{ fontSize: 16 }} /></IconButton>
                          </InputAdornment>
                        ) }),
                      }} />

                    
             

                    {/* Filter by: label + pill-button filters */}
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500, whiteSpace: 'nowrap' }}>
                      Filter by:
                    </Typography>
                    {[
                      { label: 'Author',        key: 'authors', total: 3, items: [['me','Me'],['following','Following'],['others','Others']] },
                      { label: 'Activity Type', key: 'types',   total: 4, items: [['post','Posts'],['comment','Comments'],['consortium','Consortium'],['suggestion','Suggestions']] },
                    ].map(({ label, key, total, items }) => {
                      const isFiltered = feedFilters[key].length < total;
                      return (
                        <Button
                          key={key}
                          onClick={(e) => { setFilterMenuAnchor(e.currentTarget); setFilterMenuKey(key); }}
                          sx={{
                            borderRadius: '20px',
                            fontWeight: 500,
                            fontSize: '1rem',
                            height: 32,
                            px: '14px',
                            minWidth: 0,
                            textTransform: 'none',
                            color: isFiltered ? 'text.secondary': 'primary.main' ,
                            // the brand tint, not a grey: one colour family on the feed
                            bgcolor: (t) => alpha(t.palette.primary.main, isFiltered ? 0.12 : 0.07),
                            '&:hover': {
                              bgcolor: (t) => alpha(t.palette.primary.main, isFiltered ? 0.2 : 0.12),
                            },
                          }}
                        >
                          {label}
                        </Button>
                      );
                    })}

                    {/* Shared dropdown menu for filter buttons */}
                    <Menu
                      anchorEl={filterMenuAnchor}
                      open={Boolean(filterMenuAnchor)}
                      onClose={() => { setFilterMenuAnchor(null); setFilterMenuKey(null); }}
                      PaperProps={{ sx: { mt: 0.5, borderRadius: 1.5, minWidth: 160, border: '1px solid', borderColor: 'divider', boxShadow: '0 4px 16px rgba(0,0,0,0.12)' } }}
                    >
                      {filterMenuKey && ({
                        authors: [['me','Me'],['following','Following'],['others','Others']],
                        types:   [['post','Posts'],['comment','Comments'],['consortium','Consortium'],['suggestion','Suggestions'],['lab','Labs']],
                      }[filterMenuKey] || []).map(([v, l]) => (
                        <MenuItem
                          key={v}
                          onClick={() => setFeedFilters(prev => ({
                            ...prev,
                            [filterMenuKey]: prev[filterMenuKey].includes(v)
                              ? prev[filterMenuKey].filter(x => x !== v)
                              : [...prev[filterMenuKey], v],
                          }))}
                          sx={{ fontSize: '0.875rem', gap: 0.5 }}
                        >
                          <Checkbox
                            checked={feedFilters[filterMenuKey]?.includes(v) ?? false}
                            size="small"
                            sx={{ p: 0 }}
                          />
                          {l}
                        </MenuItem>
                      ))}
                    </Menu>

                    {/* Archived: the icon alone, highlighted while archived entries are shown */}
                    <Tooltip title={feedFilters.archived ? 'Hide archived' : 'Show archived'}>
                      <IconButton onClick={toggleArchived} aria-pressed={feedFilters.archived}
                        aria-label={feedFilters.archived ? 'Hide archived' : 'Show archived'}
                        sx={{ width: 32, height: 32, color: feedFilters.archived ? 'primary.main' : 'text.secondary',
                          bgcolor: feedFilters.archived ? alpha(theme.palette.primary.main, 0.12) : 'transparent',
                          '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.08) } }}>
                        <ArchiveIcon sx={{ fontSize: 14 }} />
                      </IconButton>
                    </Tooltip>
                  </Box>
                    <OpinionSharingCard onPost={handleCreatePost} />


                    </Box>
                {/* </CardContent> */}
              {/* </OpinionCard> */}

              {/* Activity Feed: no box around it; each post is its own shaded card on the page, in line with the
                  composer above */}
              <Card sx={{ borderRadius: 0, boxShadow: 'none', border: 'none', 
                bgcolor: 'transparent', minWidth: 0, overflow: 'visible' }}>
                <CardContent sx={{ p: '0 !important' }}>
                  {/* Activity Feed header + inline filters */}
   

                  {( () => {
                    // Combine all activities into single sentences
                    const allActivities = [];

                    // 1. "X suggested you this" - received suggestions
                    receivedSuggestions.forEach(suggestion => {
                      allActivities.push({
                        id: `suggestion-${suggestion.id}`,
                        text: `${suggestion.author_info?.first_name || 'Someone'} ${suggestion.author_info?.last_name || ''} suggested you to check out ${suggestion.target_name || 'a project'}`,
                        timestamp: suggestion.created,
                        type: 'suggestion',
                        icon: <SendIcon sx={{ fontSize: 16, color: '#ad89ad' }} />,
                        sourceUrl: suggestion.target_type === 'call' ? `/funding/call/${suggestion.target_id}` :
                                   suggestion.target_type === 'post' ? `/posts/${suggestion.target_id}` :
                                   suggestion.target_type === 'suggestion' ? `/suggestions/${suggestion.target_id}` :
                                   suggestion.target_type === 'reflection' ? `/reflections/${suggestion.target_id}` :
                                   undefined
                      });
                    });

                    // 2. "You liked that" - recent likes
                    

                  //   recentLikes.forEach(like =>  {
                  //     //  {console.log('Processing like activity:', like);
                  //     const targetName = like.content_type === 'call' ? 'a funding call' :
                  //                      like.content_type === 'comment' ? 'a comment' :
                  //                      like.content_type === 'suggestion' ? 'a suggestion' :
                  //                      like.content_type === 'reflection' ? 'a reflection' : 'something';
                      
                  //     const likees = like.user === currentUser.id ? 'your' : `${like.user_info?.name}'s` || "someone's";
                      
                  //     allActivities.push({
                  //       id: `like-${like.id}`,
                  //       text: `You liked ${likees} ${like.content_type}`,
                  //       timestamp: like.created,
                  //       type: 'like',
                  //       icon: <ThumbUpIcon sx={{ fontSize: 16, color: 'info.main' }} />
                  //     });
                  // });

                    // 3. "You joined consortium Z" - joined consortia
                    joinedConsortia.forEach(membership => {
                      // { console.log("**** membership", membership) }
                      allActivities.push({
                        id: `consortium-${membership.id}`,
                        text: `You joined consortium "${membership.consortium.name || "Unknown Consortium"}"`,
                        timestamp: membership.created,
                        type: 'consortium',
                        icon: <GroupIcon sx={{ fontSize: 16, color: 'primary.main' }} />,
                        sourceUrl: `/consortium/${membership.consortium.id}`

                      });
                    });

                    // 4. Comments - "You commented on X"
                    recentComments.forEach(comment => {
                      allActivities.push({
                        id: `comment-${comment.id}`,
                        text: `You commented on a ${comment.target_type === 'call' ? 'funding call' : comment.target_type}: "${comment.body.substring(0, 50)}${comment.body.length > 50 ? '...' : ''}"`,
                        timestamp: comment.created,
                        type: 'comment',
                        icon: <CommentIcon sx={{ fontSize: 16, color: 'primary.light' }} />,
                        sourceUrl: comment.target_type === 'call' ? `/funding/call/${comment.target_id}` :
                                   comment.target_type === 'post' ? `/feed/${comment.author_info.id}#post-${comment.target_id}` :
                                   comment.target_type === 'suggestion' ? `/suggestions/${comment.target_id}` :
                                   comment.target_type === 'reflection' ? `/reflections/${comment.target_id}` :
                                   undefined
                      });
                    });

                    // 5. Posts - "You posted: 'title' or first part of body"
                    userPosts.forEach(post => {
                      const postText = post?.title ? `"${post.title}"` : `"${post.body.substring(0, 50)}${post.body.length > 50 ? '...' : ''}"`;
                      allActivities.push({
                        id: `post-${post.id}`,
                        text: `You: ${postText}`,
                        timestamp: post.created,
                        type: 'post',
                        icon: <EditIcon sx={{ fontSize: 16, color: 'primary.light' }} />,
                        postId: post.id,
                        postData: post,
                        sourceUrl: `/feed/${post.author_id}/posts/${post.id}`
                      });
                    });

                    // 6. Followed activities - "Activity from entities you follow"
                    followedActivities.forEach(activity => {

                      if (!allActivities.some(a => a.postId === activity.id)) {

                      if (activity.activity_type === 'followed_post') {
                        const postText = activity.title ? `"${activity.title}"` : 
                            `"${activity.body.substring(0, 50)}${activity.body.length > 50 ? '...' : ''}"`;
                        
                        allActivities.push({
                          id: `followed-post-${activity.id}`,
                          text: `${activity.author_name || 'Someone'} posted: ${postText}`,
                          timestamp: activity.created,
                          type: 'followed_post',
                          icon: <PersonIcon sx={{ fontSize: 16, color: 'info.main' }} />,
                          postId: activity.id,
                          postData: activity,
                          sourceUrl: `/feed/${activity.author_id}/posts/${activity.id}` 
                        });
                      } else if (activity.activity_type === 'followed_consortium_update') {
                        allActivities.push({
                          id: activity.id,
                          text: activity.text,
                          timestamp: activity.created,
                          type: 'followed_consortium_update',
                          icon: <GroupIcon sx={{ fontSize: 16, color: 'success.main' }} />
                        });
                      } else if (activity.activity_type === 'followed_lab_update') {
                        allActivities.push({
                          id: activity.id,
                          text: activity.text,
                          timestamp: activity.timestamp,
                          type: 'followed_lab_update',
                          icon: <LabIcon sx={{ fontSize: 16, color: /^#[0-9a-f]{6}$/i.test(activity.lab?.color || '') ? activity.lab.color : '#5b5fc7' }} />,
                          sourceUrl: `/lab/${activity.lab?.id}/`,
                        });
                      }
                    }}
                  );

                    // 7. Posts of people you neither follow nor share a consortium with ("Others")
                    const followedIds = new Set(following.filter(f => f.content_type === 'user').map(f => String(f.object_id)));
                    otherPosts.forEach(post => {
                      const authorId = String(post.author?.id || post.author_id || post.author || '');
                      if (!authorId || authorId === String(userProfile?.id) || followedIds.has(authorId)) return;
                      if (allActivities.some(a => a.postId === post.id)) return;
                      allActivities.push({
                        id: `other-post-${post.id}`,
                        text: `${post.author_name || 'Someone'} posted`,
                        timestamp: post.created,
                        type: 'other_post',
                        postId: post.id,
                        postData: post,
                        notFollowing: true,
                      });
                    });

                    // Sort by timestamp (most recent first)
                    allActivities.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));

                    // Author and type mappings for each activity record
                    const ACTIVITY_AUTHOR = {
                      post:                       'me',
                      comment:                    'me',
                      consortium:                 'me',
                      followed_post:              'following',
                      followed_consortium_update: 'following',
                      followed_lab_update:        'following',
                      suggestion:                 'others',
                      other_post:                 'others',
                    };
                    const ACTIVITY_TYPE = {
                      post:                       'post',
                      comment:                    'comment',
                      consortium:                 'consortium',
                      followed_post:              'post',
                      followed_consortium_update: 'consortium',
                      followed_lab_update:        'lab',
                      suggestion:                 'suggestion',
                      other_post:                 'post',
                    };

                    // the search box: every word must appear in the entry (its text, the post, its author, a link)
                    const words = feedSearch.toLowerCase().split(/\s+/).filter(Boolean);
                    const searchable = (a) => {
                      const p = a.postData || {};
                      return [typeof a.text === 'string' ? a.text : '', p.title, p.body, p.author_name, p.original?.body,
                        p.original?.author_name, p.link_preview?.title, p.link_preview?.url].filter(Boolean).join(' ').toLowerCase();
                    };
                    const visibleActivities = allActivities.filter(a => {
                      const isArchived = hiddenActivities.has(a.id);
                      if (isArchived) return feedFilters.archived;
                      return feedFilters.authors.includes(ACTIVITY_AUTHOR[a.type] ?? 'others')
                          && feedFilters.types.includes(ACTIVITY_TYPE[a.type] ?? a.type)
                          && (!words.length || words.every(w => searchable(a).includes(w)));
                    });
                    const hasMoreLoaded = visibleActivities.length > visibleCount;
                    const showMore = async () => {
                      if (hasMoreLoaded) { setVisibleCount(c => c + FEED_PAGE); return; }
                      if (noOlder || loadingMore || !userProfile?.id || depthRef.current >= MAX_DEPTH) return;
                      // everything loaded is shown: fetch further back
                      const before = userPosts.length + followedActivities.length + otherPosts.length;
                      setLoadingMore(true);
                      depthRef.current += 1;
                      const [mine, followed, others] = await Promise.all([
                        fetchUserPosts(userProfile.id), fetchFollowedActivities(), fetchOtherPosts()]);
                      setLoadingMore(false);
                      setVisibleCount(c => c + FEED_PAGE);
                      if ((mine?.length || 0) + (followed?.length || 0) + (others?.length || 0) <= before) setNoOlder(true);
                    };

                    // until every source of the first load is back: placeholders, then all entries at once
                    if (!feedReady) {
                      return (
                        <Box aria-busy="true" sx={{ py: 4, px: 1 }}>
                          <LinearProgress sx={{ maxWidth: 320, mx: 'auto', mb: 1.5, borderRadius: 1 }} />
                          <Typography variant="body2" color="text.secondary" textAlign="center">Loading your feed…</Typography>
                        </Box>
                      );
                    }

                    return visibleActivities.length === 0 ? (
                      <Box textAlign="center" py={4}>
                        <TrendingUpIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                        <Typography variant="body2" color="text.secondary">
                          {feedFilters.archived && hiddenActivities.size === 0
                            ? 'No archived activities'
                            : !feedFilters.archived && hiddenActivities.size > 0
                            ? 'All matching activities archived — use the archive icon to show them'
                            : words.length ? `Nothing in the feed matches “${feedSearch.trim()}”.` : 'No recent activity yet'}
                        </Typography>
                      </Box>
                    ) : (
                      <Stack spacing={0.5}>
                        {visibleActivities.slice(0, visibleCount).map((activity) => {

                          // Render followed posts using PostItem component
                          if (activity.type.includes('post') && activity.postData) {
                            return (
                              <PostItem
                                key={activity.id}
                                post={activity.postData}
                                currentUser={currentUser}
                                compact={true}
                                showNetworkLabel={true}
                                showFollow={Boolean(activity.notFollowing)}
                                onRemove={() => handleRemoveActivity(activity.id)}
                              />
                            );
                          }
                          
                          // Render other activities as before
                          return (
                            <Box
                              key={activity.id}
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1.5,
                                p: 1.5,
                                borderRadius: 1,
                                bgcolor: 'transparent',
                                ...ROW_HOVER_SX,
                                cursor: activity.sourceUrl ? 'pointer' : 'default'
                              }}
                            >
                              {activity.icon}
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }} flex={1} onClick={activity.sourceUrl ? () => navigate(activity.sourceUrl) : undefined}>
                                <Typography variant="feedBody1" sx={{ lineHeight: 1.4 }}>
                                  {activity.text}
                                </Typography>


                                <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                                  {activity.timestamp && !isNaN(new Date(activity.timestamp).getTime())
                                    ? formatDistanceToNow(new Date(activity.timestamp), { addSuffix: true })
                                    : ''}
                                </Typography>
                          
                           
                              </Box>
                              {hiddenActivities.has(activity.id) ? (
                                <IconButton
                                  size="small"
                                  onClick={() => setHiddenActivities(prev => { const s = new Set(prev); s.delete(activity.id); return s; })}
                                  title="Restore from archive"
                                  sx={{ opacity: 0, transition: 'opacity 0.2s', '.MuiBox-root:hover > &': { opacity: 1 }, '&:hover': { opacity: 1 }, ml: 0.5 }}
                                >
                                  <VisibilityOffIcon sx={{ fontSize: 16 }} />
                                </IconButton>
                              ) : (
                                <IconButton
                                  size="small"
                                  onClick={() => handleRemoveActivity(activity.id)}
                                  title="Archive — remove from feed permanently"
                                  sx={{ opacity: 0, transition: 'opacity 0.2s', '.MuiBox-root:hover > &': { opacity: 1 }, '&:hover': { opacity: 1, color: 'text.secondary' }, ml: 0.5 }}
                                >
                                  <CloseIcon sx={{ fontSize: 16 }} />
                                </IconButton>
                              )}
                            </Box>
                          );
                        })}
                        <FeedMore onMore={showMore} loading={loadingMore}
                          done={!hasMoreLoaded && (noOlder || depthRef.current >= MAX_DEPTH)} />
                      </Stack>
                    );
                  })()}
                </CardContent>
              </Card>
            </Stack>
          </Grid>

          {/* Right Column - Quick Actions, Recent Activity: its own column on wide screens and after the feed on
              phones; at medium widths (sidebar + two columns) it continues the left column instead */}
          {!midWidth && (
            <Grid item xs={columns === 3 ? undefined : 16} sx={rightColSx}>
              {rightColumnItems}
            </Grid>
          )}
        </Grid>
      )}
      
      {/* Privacy Settings Dialog */}
      {/* <PrivacySettingsDialog
        open={privacyDialogOpen}
        onClose={() => setPrivacyDialogOpen(false)}
        onSave={() => {
          // Settings saved successfully
        }}
      /> */}
      {guide.open && (
        <GuideTour steps={FEED_GUIDE_STEPS} step={guide.step} targetRect={guide.targetRect}
          onNext={guide.next} onClose={guide.close} ariaLabel="Activity Feed guide" />
      )}
    </PageContainer>
  );
};

export default AlternativeProfileFeed;